import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Mail, Lock, Phone, CreditCard, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    password: '',
    confirmPassword: '',
    dni: '',
    telefono: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/register-student`, {
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        email: formData.email,
        password: formData.password,
        dni: formData.dni || undefined,
        telefono: formData.telefono || undefined
      });
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div 
        className="min-h-screen relative bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/7722527/pexels-photo-7722527.jpeg)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#004E89]/80 to-[#0EA5E9]/70"></div>
        <div className="relative z-10 w-full max-w-md p-6">
          <div className="bg-white rounded-lg shadow-2xl p-8 text-center">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h1 className="font-manrope font-bold text-2xl text-slate-900 mb-2">
              ¡Registro completado!
            </h1>
            <p className="text-slate-600 mb-6">
              Tu cuenta ha sido creada y está pendiente de validación por el administrador. 
              Te notificaremos cuando tu cuenta esté activa.
            </p>
            <Link to="/login">
              <Button className="bg-[#004E89] hover:bg-[#003B66]">
                Ir a Iniciar Sesión
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative bg-cover bg-center"
      style={{ backgroundImage: 'url(https://images.pexels.com/photos/7722527/pexels-photo-7722527.jpeg)' }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#004E89]/80 to-[#0EA5E9]/70"></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_medcert-2/artifacts/7ilthot6_Logo%20v0.1.JPG" 
              alt="IRTA Logo" 
              className="w-48 mx-auto bg-white p-4 rounded-lg shadow-lg"
            />
          </div>

          {/* Register Form */}
          <div className="bg-white rounded-lg shadow-2xl p-8">
            <div className="mb-6">
              <Link to="/login" className="inline-flex items-center text-[#004E89] hover:underline text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Volver al inicio de sesión
              </Link>
            </div>

            <h1 className="font-manrope font-bold text-2xl text-slate-900 mb-2">
              Crear cuenta
            </h1>
            <p className="font-inter text-slate-600 mb-6">
              Regístrate como alumno para acceder a la formación
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      className="pl-10"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Apellidos *</Label>
                  <Input
                    value={formData.apellidos}
                    onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Correo electrónico *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    type="email"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>DNI/NIE</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      className="pl-10"
                      value={formData.dni}
                      onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      className="pl-10"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Contraseña *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    type="password"
                    className="pl-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <Label>Confirmar contraseña *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    type="password"
                    className="pl-10"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#004E89] hover:bg-[#003B66]"
                disabled={loading}
              >
                {loading ? 'Registrando...' : 'Crear cuenta'}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-6">
              Tu cuenta será validada por un administrador antes de poder acceder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
